import { Button } from 'antd';
import clsx from 'clsx';
import { ArrowLeft, RotateCw, TriangleAlert } from 'lucide-react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';

interface RouteErrorPageProps {
  /** Outside the office and yard shells (a layout or auth failed), so the page has to stand on its own. */
  fullScreen?: boolean;
}

function describe(error: unknown): string {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return error.message;
  return String(error);
}

/**
 * What a crashed page shows in place of React Router's developer error screen. Inside a shell it replaces
 * only the page, so the navigation still works, and the next navigation clears it. The error itself is
 * shown only in development; React Router has already logged it to the console.
 */
export function RouteErrorPage({ fullScreen = false }: RouteErrorPageProps) {
  const error = useRouteError();
  const navigate = useNavigate();

  return (
    <div className={clsx('grid place-items-center px-6', fullScreen ? 'min-h-screen bg-page' : 'py-16')}>
      <div role="alert" className="flex max-w-lg flex-col items-center text-center">
        <div className="mb-5 grid size-12 place-items-center rounded-xl bg-error/10 text-error ring-1 ring-error/20">
          <TriangleAlert className="size-5" strokeWidth={1.75} />
        </div>
        <h1 className="text-xl font-semibold tracking-tight text-fg">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted">
          This page hit an unexpected error. Nothing you saved has been lost. Reload to try again, or go back to dispatch.
        </p>
        {import.meta.env.DEV && (
          <pre className="mt-6 max-w-full overflow-x-auto rounded-lg bg-surface p-3 text-left font-mono text-xs text-error">{describe(error)}</pre>
        )}
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          <Button type="primary" icon={<RotateCw className="size-4" />} onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Button icon={<ArrowLeft className="size-4" />} onClick={() => navigate('/')}>
            Back to dispatch
          </Button>
        </div>
      </div>
    </div>
  );
}
